import { useScrollReveal } from '../hooks/useScrollReveal';
import { useEffect, useState } from 'react';

const flowSteps = [
  { id: '01', title: '课堂材料', desc: '视频 / 音频 / 课件 / 文本\n多源材料输入', icon: 'ri-folder-video-line', color: 'primary' as const },
  { id: '02', title: '多模态识别', desc: '文字、声音、画面\n多模态理解', icon: 'ri-file-music-line', color: 'primary' as const },
  { id: '03', title: '指标映射', desc: '关键片段对应\n评价维度', icon: 'ri-map-pin-2-line', color: 'primary' as const },
  { id: '04', title: '专用模型评价', desc: '训练后的思政课\n专用评价模型', icon: 'ri-brain-line', color: 'accent' as const },
  { id: '05', title: '证据链', desc: '原话、时间戳、\n课件页、视频片段', icon: 'ri-links-line', color: 'primary' as const },
  { id: '06', title: '总分 / 维度分 / 分档', desc: '结构化结果输出\n可视化呈现', icon: 'ri-bar-chart-grouped-line', color: 'accent' as const },
  { id: '07', title: '改进建议', desc: '面向教师的\n具体优化建议', icon: 'ri-lightbulb-line', color: 'primary' as const },
];

export default function Section5Flow({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const [visibleSteps, setVisibleSteps] = useState(0);

  useEffect(() => {
    if (!titleVisible) { setVisibleSteps(0); return; }
    let step = 0;
    const t = setInterval(() => {
      step += 1;
      setVisibleSteps(step);
      if (step >= flowSteps.length) clearInterval(t);
    }, 180);
    return () => clearInterval(t);
  }, [titleVisible]);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      {/* 三层背景光效 */}
      <div className="absolute top-1/4 left-[8%] w-[380px] h-[380px] rounded-full bg-primary-100/12 blur-[130px] pointer-events-none animate-orb-float-1" />
      <div className="absolute top-2/3 right-[10%] w-[350px] h-[350px] rounded-full bg-accent-100/10 blur-[120px] pointer-events-none animate-orb-float-2" />
      <div className="absolute top-1/2 left-[55%] w-[300px] h-[300px] rounded-full bg-secondary-100/8 blur-[140px] pointer-events-none animate-orb-float-3" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 py-10 md:py-16">
        {/* 标题区 */}
        <div ref={titleRef} className="text-center mb-10 md:mb-14">
          <span
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-50/80 border border-primary-200/40 text-xs font-body font-semibold text-primary-600 tracking-[0.1em] mb-4 transition-all duration-700 ${
              titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <i className="ri-node-tree text-sm" />
            AI评课流程
          </span>
          <h2
            className={`font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-3 transition-all duration-1000 delay-100 ${
              titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            从理解课堂到<span className="bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text text-transparent">形成评价结果</span>
          </h2>
          <p
            className={`text-sm md:text-base text-foreground-950 font-body max-w-lg mx-auto leading-relaxed transition-all duration-1000 delay-200 ${
              titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            AI评课不是把一节课压缩成一句评语，而是"结果 + 证据"同时出现
          </p>
        </div>

        {/* 桌面端：7步横排等宽 */}
        <div className="hidden md:flex items-start justify-center gap-0">
          {flowSteps.map((step, i) => {
            const isGold = step.color === 'accent';
            const isHighlighted = step.id === '04' || step.id === '06';
            const revealed = visibleSteps > i;

            return (
              <div key={step.id} className="flex items-start flex-1 max-w-[180px]">
                {/* 卡片 */}
                <div
                  className={`flex flex-col items-center text-center w-full transition-all duration-500 ${
                    revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                  }`}
                  style={{ transitionDelay: `${i * 100}ms` }}
                >
                  {/* 编号 */}
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center mb-3 text-sm font-bold font-mono text-white ${
                      isGold ? 'bg-accent-500' : 'bg-primary-500'
                    }`}
                  >
                    {step.id}
                  </div>

                  {/* 卡片本体 */}
                  <div
                    className={`w-full rounded-xl px-3 py-4 border transition-all duration-300 cursor-default hover:-translate-y-1 ${
                      isHighlighted
                        ? 'glass-liquid-gold border-accent-200/30'
                        : 'glass-liquid border-primary-200/30 hover:border-primary-300/50'
                    }`}
                  >
                    <div
                      className={`w-11 h-11 rounded-xl flex items-center justify-center mx-auto mb-2.5 ${
                        isGold ? 'bg-accent-100' : 'bg-primary-100'
                      }`}
                    >
                      <i className={`${step.icon} text-xl ${isGold ? 'text-accent-600' : 'text-primary-600'}`} />
                    </div>
                    <h4 className="font-body text-xs font-bold text-foreground-950 mb-1.5 leading-snug px-1">
                      {step.title}
                    </h4>
                    <p className="text-[11px] text-foreground-950 font-body leading-relaxed whitespace-pre-line">
                      {step.desc}
                    </p>
                  </div>
                </div>

                {/* 箭头（卡片之间） */}
                {i < flowSteps.length - 1 && (
                  <div className="flex items-center justify-center flex-shrink-0 w-6 mt-16">
                    <i className="ri-arrow-right-line text-primary-300 text-base" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* 移动端：单列时间线 */}
        <div className="md:hidden space-y-3">
          {flowSteps.map((step, i) => {
            const isGold = step.color === 'accent';
            const revealed = visibleSteps > i;
            return (
              <div
                key={step.id}
                className={`flex items-start gap-3 transition-all duration-500 ${
                  revealed ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
                }`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                {/* 左侧时间线 */}
                <div className="flex flex-col items-center flex-shrink-0">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold font-mono text-white ${
                      isGold ? 'bg-accent-500' : 'bg-primary-500'
                    }`}
                  >
                    {step.id}
                  </div>
                  {i < flowSteps.length - 1 && (
                    <div className="w-px flex-1 min-h-[20px] border-l border-dashed border-primary-300/40 mt-1" />
                  )}
                </div>

                {/* 右侧卡片 */}
                <div
                  className={`flex-1 rounded-xl px-4 py-3 border ${
                    step.id === '04' || step.id === '06'
                      ? 'bg-accent-50/50 border-accent-200/40'
                      : 'bg-background-100/70 border-background-200/40'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <i className={`${step.icon} text-sm ${isGold ? 'text-accent-500' : 'text-primary-500'}`} />
                    <h4 className="font-body text-sm font-bold text-foreground-950">{step.title}</h4>
                  </div>
                  <p className="text-xs text-foreground-950 font-body leading-relaxed whitespace-pre-line">
                    {step.desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}